import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';

interface CollaboratorDeleteModalProps {
  collaboratorName?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDeleting?: boolean;
}

const CollaboratorDeleteModal: React.FC<CollaboratorDeleteModalProps> = ({
  collaboratorName,
  onConfirm,
  onCancel,
  isDeleting = false
}) => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-red-600 dark:text-red-400 flex items-center">
            <AlertTriangle className="mr-2" size={20} />
            {t('company.collaborators.delete.title', 'Supprimer le collaborateur')}
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
            disabled={isDeleting}
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            {t('company.collaborators.delete.confirmation', 'Êtes-vous sûr de vouloir supprimer le collaborateur')} <strong>{collaboratorName}</strong>?
          </p>

          <p className="text-gray-700 dark:text-gray-300">
            {t('company.collaborators.delete.warning', 'Cette action est irréversible. Le collaborateur perdra l\'accès à cette entreprise.')}
          </p>

          <div className="bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 p-4 mt-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-yellow-700 dark:text-yellow-400">
                  {t('company.collaborators.delete.dataWarning', 'Le collaborateur ne pourra plus accéder aux données et aux services de cette entreprise.')}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              disabled={isDeleting}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('company.collaborators.delete.cancel', 'Annuler')}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isDeleting}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  {t('company.collaborators.delete.deleting', 'Suppression...')}
                </>
              ) : (
                t('company.collaborators.delete.confirm', 'Supprimer définitivement')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollaboratorDeleteModal;
